import { useCallback } from 'react'
import { useUpdateFeatureStateMutation } from 'common/services/useFeatureState'
import { useCreateAndSetFeatureVersionMutation } from 'common/services/useFeatureVersion'
import type {
  Environment,
  FeatureState,
  ProjectFlag,
} from 'common/types/responses'

type ToggleFeatureOptions = {
  successMessage?: string
  errorMessage?: string
  onError?: (error: unknown) => void
  onSuccess?: () => void
}

/** Toggles a feature flag's enabled state with toast notifications. */
export const useToggleFeatureWithToast = () => {
  const [updateFeatureState, updateState] = useUpdateFeatureStateMutation()
  const [createAndSetFeatureVersion, versionState] =
    useCreateAndSetFeatureVersionMutation()

  const toggleWithToast = useCallback(
    async (
      flag: ProjectFlag,
      environmentFlag: FeatureState | undefined,
      environment: Environment,
      options?: ToggleFeatureOptions,
    ) => {
      if (!environmentFlag) {
        console.warn('Cannot toggle feature: environmentFlag is undefined')
        options?.onError?.(new Error('environmentFlag is undefined'))
        return
      }
      try {
        if (environment.use_v2_feature_versioning) {
          // Versioned environment: use versioning API
          await createAndSetFeatureVersion({
            environmentApiKey: environment.api_key,
            environmentId: environment.id,
            featureId: flag.id,
            featureStates: [
              {
                ...environmentFlag,
                enabled: !environmentFlag.enabled,
              },
            ],
            projectId: flag.project,
          }).unwrap()
        } else {
          // Non-versioned environment: use simple PUT
          await updateFeatureState({
            body: {
              enabled: !environmentFlag.enabled,
            },
            environmentFlagId: environmentFlag.id,
            environmentId: environment.api_key,
          }).unwrap()
        }

        if (options?.successMessage) {
          toast(options.successMessage)
        }
        options?.onSuccess?.()
      } catch (error) {
        console.error('Failed to toggle feature:', error)
        toast(
          options?.errorMessage ||
            'Failed to toggle feature. Please try again.',
          'danger',
        )
        options?.onError?.(error)
      }
    },
    [updateFeatureState, createAndSetFeatureVersion],
  )

  const isLoading = updateState.isLoading || versionState.isLoading

  return [toggleWithToast, { isLoading }] as const
}
